import { useIntl } from '@/intl'
import { Drawer } from '@/scopes/common/components/Drawer'
import { PublicationCodes } from '@/scopes/common/domain/models'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import DestinationDropdown from '@/scopes/destinations/components/DestinationSelector/DestinationDropdown'
import { destinationOptionsSelector } from '@/scopes/destinations/domain/selectors'
import { useDestinationActions } from '@/scopes/destinations/infrastructure/depedencies/dependencies'
import { useDestinationStoreSelector } from '@/scopes/destinations/infrastructure/store/useDestinationStoreSelector'
import { IndeterminateProgressCircle } from '@sunwebgroup/uds-ui/IndeterminateProgressCircle'
import cn from 'classnames'
import { isEmpty } from 'ramda'
import * as React from 'react'
import styles from './DestinationSelector.module.css'

export default function DestinationSelector() {
  const intl = useIntl()
  const { destinationSelected } = useDestinationActions()
  const { results, isLoading } = useDestinationStoreSelector(destinationOptionsSelector)
  const publicationCode = useConfiguration('publicationCode')
  const defaultDropdownCount = PublicationCodes.isElizaPublicationCode(publicationCode) ? 2 : 3
  const season = PublicationCodes.isSunwebWinterPublicationCode(publicationCode) ? 'winter' : 'summer'

  const regionPlaceholderIdBySeason = {
    winter: 'destination_placeholder_item_region__winter',
    summer: 'destination_placeholder_item_region__summer',
  }
  const regionLabelIdBySeason = {
    winter: 'destination_dropdown_label_region__winter',
    summer: 'destination_dropdown_label_region__summer',
  }
  const cityPlaceholderIdBySeason = {
    winter: 'destination_placeholder_item_city__winter',
    summer: 'destination_placeholder_item_city__summer',
  }
  const cityLabelIdBySeason = {
    winter: 'destination_dropdown_label_city__winter',
    summer: 'destination_dropdown_label_city__summer',
  }

  const { countries, regions, subregions, cities } = results

  const optionChangeHandler = (value?: string) => {
    destinationSelected({ url: value })
  }

  const dropdownConfigs = [
    {
      id: 'country-destination',
      options: countries,
      placeholderId: 'destination_placeholder_item_country',
      labelId: 'destination_dropdown_label_country',
      visible: !isEmpty(countries),
    },
    {
      id: 'region-destination',
      options: regions,
      placeholderId: regionPlaceholderIdBySeason[season],
      labelId: regionLabelIdBySeason[season],
      visible: !isEmpty(regions),
    },
    {
      id: 'subregion-destination',
      options: subregions,
      placeholderId: 'destination_placeholder_item_subregion',
      labelId: 'destination_dropdown_label_subregion',
      visible: !isEmpty(subregions) && season === 'winter',
    },
    {
      id: 'city-destination',
      options: cities,
      placeholderId: cityPlaceholderIdBySeason[season],
      labelId: cityLabelIdBySeason[season],
      visible: !isEmpty(cities),
    },
  ]

  const visibleDropdowns = dropdownConfigs.filter(({ visible }) => visible)
  const numberOfItemsClass = { 1: styles.one, 2: styles.two, 3: styles.three, 4: styles.four }[visibleDropdowns.length]
  const defaultDropdownCountClass = { 2: styles.defaultTwo, 3: styles.defaultThree }[defaultDropdownCount]

  return (
    <Drawer className={cn(styles.drawer, numberOfItemsClass, defaultDropdownCountClass)}>
      {isLoading && (
        <div className={styles.loading}>
          <IndeterminateProgressCircle size={32} className={styles.loader} />
        </div>
      )}
      {!isLoading && (
        <Drawer.Items className={styles.items}>
          {dropdownConfigs
            .filter(({ visible }) => visible)
            .map(({ id, options, placeholderId, labelId }) => (
              <DestinationDropdown
                key={id}
                id={id}
                placeholder={intl.formatMessage({ id: placeholderId })}
                options={options}
                label={intl.formatMessage({ id: labelId })}
                handleChange={optionChangeHandler}
                disabled={isLoading}
              />
            ))}
        </Drawer.Items>
      )}
    </Drawer>
  )
}
